import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WifiIcon from '@mui/icons-material/Wifi';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { formatRadioProfileOptionLabel } from '../utils/radioProfileBand';
import { formatSsidProfileOptionLabel } from '../utils/ssidProfileLabel';
import {
  buildCapDeviceTreeIdsForGroup,
  capUpperTreeIdsFromConfig,
  DEFAULT_CAP_TREE_ID,
  isJnet2CapDevice,
  ssidBandNeedsCapTreeSelect,
  showApiWarnings,
} from '../utils/publicWifiRrmPlanUi';
import {
  MapSelectionDialogBody,
  MapSelectionDialogTabs,
  MapSelectionDialogTitle,
  MapSelectionSummaryStrip,
  mapSelectionDialogPaperProps,
} from './mapSelectionDialogUi';

const PUBLIC_WIFI_SELECTION_TABS = [
  { label: 'Create group' },
  { label: 'SSID' },
  { label: 'Radio profile' },
  { label: 'Add to group' },
];

function summarizeApplyResults(enqueueSnackbar, label, applyResults, totalRequested) {
  const results = applyResults || {};
  let succeeded = 0;
  let failed = 0;
  let skipped = 0;
  Object.values(results).forEach((status) => {
    if (typeof status !== 'string') return;
    if (status === 'success') succeeded += 1;
    else if (status.startsWith('failed')) failed += 1;
    else skipped += 1;
  });
  const accounted = succeeded + failed + skipped;
  if (totalRequested > 0 && accounted === 0) {
    enqueueSnackbar(
      `${label}: 0 of ${totalRequested} device(s) reached the apply phase (unsupported device type or missing configuration)`,
      { variant: 'info' },
    );
    return;
  }
  if (failed === 0 && skipped === 0) {
    enqueueSnackbar(`${label} applied to ${succeeded} device(s)`, { variant: 'success' });
  } else if (failed === 0) {
    enqueueSnackbar(`${label} applied to ${succeeded} device(s); ${skipped} skipped`, { variant: 'info' });
  } else {
    enqueueSnackbar(`${label}: ${succeeded} ok, ${failed} failed, ${skipped} skipped`, { variant: 'warning' });
  }
}

const PublicWifiMapSelectionDialog = ({
  open,
  onClose,
  selectedDevices,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogTab, setDialogTab] = useState(0);
  const [ssidProfiles, setSsidProfiles] = useState([]);
  const [vlanProfiles, setVlanProfiles] = useState([]);
  const [radioProfiles, setRadioProfiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [ssidId, setSsidId] = useState('');
  const [radioProfileId, setRadioProfileId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [addGroupTreeId, setAddGroupTreeId] = useState(DEFAULT_CAP_TREE_ID);
  const [addGroupCapTreeIds, setAddGroupCapTreeIds] = useState(['5GHz_0', '5GHz_1']);
  const [addGroupConfigLoading, setAddGroupConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const bulkRequestAbortRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setDialogTab(0);
    setGroupName('');
    setSsidId('');
    setRadioProfileId('');
    setGroupId('');
    setAddGroupTreeId(DEFAULT_CAP_TREE_ID);
    setAddGroupCapTreeIds(['5GHz_0', '5GHz_1']);
    api.getSSIDProfiles()
      .then((data) => { if (!cancelled) setSsidProfiles(data?.ssids || []); })
      .catch(() => { if (!cancelled) setSsidProfiles([]); });
    api.getVLANProfiles()
      .then((data) => { if (!cancelled) setVlanProfiles(data?.vlans || []); })
      .catch(() => { if (!cancelled) setVlanProfiles([]); });
    api.getRadioProfiles()
      .then((data) => { if (!cancelled) setRadioProfiles(data?.radio_profiles || []); })
      .catch(() => { if (!cancelled) setRadioProfiles([]); });
    api.getGroups()
      .then((data) => { if (!cancelled) setGroups(data?.groups || []); })
      .catch(() => { if (!cancelled) setGroups([]); });
    return () => { cancelled = true; };
  }, [open]);

  const getSerials = useCallback(() => (
    (selectedDevices || [])
      .map((d) => d.serial_number?.toLowerCase())
      .filter(Boolean)
  ), [selectedDevices]);

  const handleClose = useCallback(() => {
    if (bulkRequestAbortRef.current) {
      bulkRequestAbortRef.current.abort();
      bulkRequestAbortRef.current = null;
    }
    onClose?.();
  }, [onClose]);

  const handleCreateGroup = useCallback(async () => {
    const serials = getSerials();
    if (!groupName.trim() || serials.length === 0) return;
    setSaving(true);
    try {
      const group = await api.createGroup({ name: groupName.trim(), devices: serials });
      if (ssidId && group?.id) {
        const ssidProfile = ssidProfiles.find((p) => p.id === ssidId);
        const deviceTreeIds = buildCapDeviceTreeIdsForGroup(selectedDevices, serials, ssidProfile?.band);
        const assignRes = await api.assignSSIDToGroup(ssidId, group.id, { deviceTreeIds });
        showApiWarnings(enqueueSnackbar, assignRes);
        enqueueSnackbar(`Group "${groupName}" created and SSID assigned`, { variant: 'success' });
      } else {
        enqueueSnackbar(`Group "${groupName}" created with ${serials.length} device(s)`, { variant: 'success' });
      }
      handleClose();
    } catch (err) {
      enqueueSnackbar(`Failed to create group: ${err?.message || err}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [getSerials, groupName, ssidId, ssidProfiles, selectedDevices, enqueueSnackbar, handleClose]);

  const handleAssignSSID = useCallback(async () => {
    const serials = getSerials();
    if (!ssidId || serials.length === 0) return;
    setSaving(true);
    const controller = new AbortController();
    bulkRequestAbortRef.current = controller;
    try {
      const res = await api.assignSSIDToDevices(ssidId, serials, { signal: controller.signal });
      if (controller.signal.aborted) return;
      showApiWarnings(enqueueSnackbar, res);
      summarizeApplyResults(enqueueSnackbar, 'SSID', res?.apply_results, serials.length);
      handleClose();
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      enqueueSnackbar(`Failed to assign SSID: ${err?.message || err}`, { variant: 'error' });
    } finally {
      if (bulkRequestAbortRef.current === controller) bulkRequestAbortRef.current = null;
      setSaving(false);
    }
  }, [getSerials, ssidId, enqueueSnackbar, handleClose]);

  const handleUnassignSSID = useCallback(async () => {
    const serials = getSerials();
    if (!ssidId || serials.length === 0) return;
    setSaving(true);
    const controller = new AbortController();
    bulkRequestAbortRef.current = controller;
    try {
      const res = await api.unassignSSIDFromDevices(ssidId, serials, { signal: controller.signal });
      if (controller.signal.aborted) return;
      summarizeApplyResults(enqueueSnackbar, 'SSID unassign', res?.apply_results, serials.length);
      handleClose();
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      enqueueSnackbar(`Failed to unassign SSID: ${err?.message || err}`, { variant: 'error' });
    } finally {
      if (bulkRequestAbortRef.current === controller) bulkRequestAbortRef.current = null;
      setSaving(false);
    }
  }, [getSerials, ssidId, enqueueSnackbar, handleClose]);

  const handleAssignRadioProfile = useCallback(async () => {
    const serials = getSerials();
    if (!radioProfileId || serials.length === 0) return;
    setSaving(true);
    const controller = new AbortController();
    bulkRequestAbortRef.current = controller;
    try {
      const res = await api.assignRadioProfileToDevices(radioProfileId, serials, { signal: controller.signal });
      if (controller.signal.aborted) return;
      showApiWarnings(enqueueSnackbar, res);
      summarizeApplyResults(enqueueSnackbar, 'Radio profile', res?.apply_results, serials.length);
      handleClose();
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      enqueueSnackbar(`Failed to assign radio profile: ${err?.message || err}`, { variant: 'error' });
    } finally {
      if (bulkRequestAbortRef.current === controller) bulkRequestAbortRef.current = null;
      setSaving(false);
    }
  }, [getSerials, radioProfileId, enqueueSnackbar, handleClose]);

  const handleUnassignRadioProfile = useCallback(async () => {
    const serials = getSerials();
    if (!radioProfileId || serials.length === 0) return;
    setSaving(true);
    const controller = new AbortController();
    bulkRequestAbortRef.current = controller;
    try {
      const res = await api.unassignRadioProfileFromDevices(radioProfileId, serials, { signal: controller.signal });
      if (controller.signal.aborted) return;
      summarizeApplyResults(enqueueSnackbar, 'Radio profile unassign', res?.apply_results, serials.length);
      handleClose();
    } catch (err) {
      if (err?.name === 'AbortError' || controller.signal.aborted) return;
      enqueueSnackbar(`Failed to unassign radio profile: ${err?.message || err}`, { variant: 'error' });
    } finally {
      if (bulkRequestAbortRef.current === controller) bulkRequestAbortRef.current = null;
      setSaving(false);
    }
  }, [getSerials, radioProfileId, enqueueSnackbar, handleClose]);

  const selectedGroupDoc = useMemo(
    () => groups.find((g) => g.id === groupId) || null,
    [groups, groupId],
  );

  const groupSsidProfiles = useMemo(() => {
    const ids = new Set((selectedGroupDoc?.ssid_ids || []).map(String));
    return (ssidProfiles || []).filter((p) => ids.has(p.id));
  }, [selectedGroupDoc, ssidProfiles]);

  const selectedCapDevices = useMemo(
    () => (selectedDevices || []).filter(isJnet2CapDevice),
    [selectedDevices],
  );

  const inheritedCapTreeId = useMemo(() => {
    if (!selectedGroupDoc?.devices?.length) return '';
    const memberSet = new Set(
      selectedGroupDoc.devices.map((s) => String(s).toLowerCase()),
    );
    for (const profile of groupSsidProfiles) {
      const treeMap = profile.device_tree_ids || {};
      for (const [serial, tree] of Object.entries(treeMap)) {
        if (memberSet.has(String(serial).toLowerCase()) && tree) return tree;
      }
    }
    return '';
  }, [selectedGroupDoc, groupSsidProfiles]);

  const addToGroupNeedsCapTree = Boolean(
    groupId
    && selectedCapDevices.length > 0
    && groupSsidProfiles.some((p) => ssidBandNeedsCapTreeSelect(p.band))
    && !inheritedCapTreeId,
  );

  const addToGroupCapConfigSource = addToGroupNeedsCapTree ? selectedCapDevices[0] : null;

  useEffect(() => {
    if (!addToGroupCapConfigSource) return undefined;
    let cancelled = false;
    setAddGroupConfigLoading(true);
    api.getDeviceConfig(addToGroupCapConfigSource.serial_number)
      .then((resp) => {
        if (cancelled) return;
        const ids = capUpperTreeIdsFromConfig(resp?.configuration);
        const options = ids.length > 0 ? ids : ['5GHz_0', '5GHz_1'];
        setAddGroupCapTreeIds(options);
        setAddGroupTreeId(
          options.includes(DEFAULT_CAP_TREE_ID) ? DEFAULT_CAP_TREE_ID : (options[0] || DEFAULT_CAP_TREE_ID),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setAddGroupCapTreeIds(['5GHz_0', '5GHz_1']);
          setAddGroupTreeId(DEFAULT_CAP_TREE_ID);
        }
      })
      .finally(() => {
        if (!cancelled) setAddGroupConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, [addToGroupCapConfigSource]);

  const handleAddToGroup = useCallback(async () => {
    const serials = getSerials();
    if (!groupId || serials.length === 0) return;
    if (addToGroupNeedsCapTree && !addGroupTreeId) {
      enqueueSnackbar('Select a CAP radio slot', { variant: 'warning' });
      return;
    }
    let deviceTreeIds;
    if (addToGroupNeedsCapTree) {
      deviceTreeIds = Object.fromEntries(
        selectedCapDevices.map((d) => [(d.serial_number || '').toLowerCase(), addGroupTreeId]),
      );
    } else if (inheritedCapTreeId && selectedCapDevices.length > 0) {
      deviceTreeIds = buildCapDeviceTreeIdsForGroup(
        selectedDevices,
        serials,
        groupSsidProfiles[0]?.band,
        inheritedCapTreeId,
      );
    }
    const body = { add_devices: serials };
    if (deviceTreeIds && Object.keys(deviceTreeIds).length > 0) {
      body.device_tree_ids = deviceTreeIds;
    }
    setSaving(true);
    try {
      const res = await api.updateGroup(groupId, body);
      showApiWarnings(enqueueSnackbar, res);
      const grp = groups.find((g) => g.id === groupId);
      enqueueSnackbar(`Added ${serials.length} device(s) to group "${grp?.name || groupId}"`, { variant: 'success' });
      const flattenApply = (nested) => {
        const flat = {};
        if (!nested || typeof nested !== 'object') return flat;
        Object.values(nested).forEach((perDevice) => {
          if (perDevice && typeof perDevice === 'object') Object.assign(flat, perDevice);
        });
        return flat;
      };
      summarizeApplyResults(enqueueSnackbar, 'SSID apply', flattenApply(res?.ssid_apply_results), serials.length);
      summarizeApplyResults(enqueueSnackbar, 'Radio apply', flattenApply(res?.radio_apply_results), serials.length);
      handleClose();
    } catch (err) {
      enqueueSnackbar(`Failed to add to group: ${err?.message || err}`, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [
    getSerials, groupId, groups, selectedDevices, selectedCapDevices, addToGroupNeedsCapTree,
    addGroupTreeId, inheritedCapTreeId, groupSsidProfiles, enqueueSnackbar, handleClose,
  ]);

  const deviceCount = selectedDevices?.length || 0;
  const selectedGroupHasProfiles = Boolean(
    selectedGroupDoc
    && ((selectedGroupDoc.ssid_ids?.length > 0) || (selectedGroupDoc.radio_profile_ids?.length > 0)),
  );

  return (
    <Dialog
      open={open}
      onClose={() => { if (!saving) handleClose(); }}
      maxWidth="md"
      fullWidth
      PaperProps={mapSelectionDialogPaperProps}
    >
      <MapSelectionDialogTitle
        title="Map selection"
        subtitle={`${deviceCount} device(s) selected — choose an action below`}
      />

      <MapSelectionDialogTabs
        value={dialogTab}
        onChange={(_, newValue) => setDialogTab(newValue)}
        tabs={PUBLIC_WIFI_SELECTION_TABS}
      />

      <MapSelectionDialogBody minHeight={340}>
        <MapSelectionSummaryStrip
          selectedDevices={selectedDevices}
          showNodeCapBreakdown={false}
        />

        {dialogTab === 0 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ color: '#7c3aed' }} />
                  Create Group
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Save the {deviceCount} selected device(s) as a named group for SSID and VLAN assignment.
                </Typography>
                <Stack spacing={3}>
                  <TextField
                    label="Group Name *"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    fullWidth
                    autoFocus
                    placeholder="e.g., Floor 2, Building A, Zone North"
                  />
                  <FormControl fullWidth>
                    <InputLabel id="pw-map-ssid-label">SSID Profile (optional)</InputLabel>
                    <Select
                      labelId="pw-map-ssid-label"
                      label="SSID Profile (optional)"
                      value={ssidId}
                      onChange={(e) => setSsidId(e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {ssidProfiles.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {formatSsidProfileOptionLabel(p, vlanProfiles)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Devices in group ({deviceCount})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selectedDevices || []).map((d) => (
                        <Chip
                          key={d.serial_number}
                          label={d.name || d.serial_number}
                          size="small"
                          color={d.connected ? 'success' : 'default'}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleCreateGroup}
                    disabled={!groupName.trim() || saving || deviceCount === 0}
                    sx={{
                      py: 1.5,
                      bgcolor: '#7c3aed',
                      '&:hover': { bgcolor: '#6d28d9' },
                      '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                    }}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {saving ? 'Creating Group…' : `Create Group with ${deviceCount} Device(s)`}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 1 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WifiIcon color="primary" />
                  SSID
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Assign or remove an SSID profile on the {deviceCount} selected device(s).
                </Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="pw-map-assign-ssid-label">SSID Profile *</InputLabel>
                  <Select
                    labelId="pw-map-assign-ssid-label"
                    label="SSID Profile *"
                    value={ssidId}
                    onChange={(e) => setSsidId(e.target.value)}
                    autoFocus
                  >
                    <MenuItem value=""><em>Choose an SSID profile…</em></MenuItem>
                    {ssidProfiles.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {formatSsidProfileOptionLabel(p, vlanProfiles)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleAssignSSID}
                    disabled={!ssidId || saving || deviceCount === 0}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {saving ? 'Assigning…' : `Assign to ${deviceCount} Device(s)`}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    color="inherit"
                    onClick={handleUnassignSSID}
                    disabled={!ssidId || saving || deviceCount === 0}
                  >
                    Unassign from {deviceCount} Device(s)
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 2 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsInputAntennaIcon color="primary" />
                  Assign Radio Profile
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Assign or remove a radio profile on the {deviceCount} selected device(s).
                </Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="pw-map-assign-radio-label">Radio Profile *</InputLabel>
                  <Select
                    labelId="pw-map-assign-radio-label"
                    label="Radio Profile *"
                    value={radioProfileId}
                    onChange={(e) => setRadioProfileId(e.target.value)}
                    autoFocus
                  >
                    <MenuItem value=""><em>Choose a radio profile…</em></MenuItem>
                    {radioProfiles.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {formatRadioProfileOptionLabel(p)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleAssignRadioProfile}
                    disabled={!radioProfileId || saving || deviceCount === 0}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {saving ? 'Assigning…' : `Assign to ${deviceCount} Device(s)`}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    color="inherit"
                    onClick={handleUnassignRadioProfile}
                    disabled={!radioProfileId || saving || deviceCount === 0}
                  >
                    Unassign from {deviceCount} Device(s)
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 3 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupAddIcon sx={{ color: '#2e7d32' }} />
                  Add to Existing Group
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add the {deviceCount} selected device(s) to an existing group.
                </Typography>
                {selectedGroupHasProfiles && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This group has SSID and/or radio profile assignments. Adding devices will apply those
                    profiles to the selected device{deviceCount === 1 ? '' : 's'} automatically.
                  </Alert>
                )}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="pw-map-add-group-label">Select Group *</InputLabel>
                  <Select
                    labelId="pw-map-add-group-label"
                    label="Select Group *"
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value)}
                    autoFocus
                  >
                    <MenuItem value=""><em>Choose a group…</em></MenuItem>
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>{g.name || g.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {addToGroupNeedsCapTree && (
                  addGroupConfigLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, mb: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="pw-map-add-cap-tree-label">CAP radio slot</InputLabel>
                      <Select
                        labelId="pw-map-add-cap-tree-label"
                        label="CAP radio slot"
                        value={addGroupTreeId}
                        onChange={(e) => setAddGroupTreeId(e.target.value)}
                      >
                        {addGroupCapTreeIds.map((id) => (
                          <MenuItem key={id} value={id}>{id}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                )}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleAddToGroup}
                  disabled={
                    !groupId
                    || saving
                    || deviceCount === 0
                    || addGroupConfigLoading
                    || (addToGroupNeedsCapTree && !addGroupTreeId)
                  }
                  sx={{
                    bgcolor: '#2e7d32',
                    '&:hover': { bgcolor: '#1b5e20' },
                  }}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {saving ? 'Adding…' : `Add ${deviceCount} Device(s) to Group`}
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}
      </MapSelectionDialogBody>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Button onClick={handleClose} disabled={saving} variant="outlined" color="inherit" fullWidth sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublicWifiMapSelectionDialog;
